import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, BuildingOfficeIcon, PencilSquareIcon, PlusCircleIcon } from '@heroicons/react/24/outline'

const ModalInvestimento = ({ states, setters, actionCreate, actionEdit }) => {
    const [nomeEmpreendimento, setNomeEmpreendimento] = useState('')
    const [error, setError] = useState(null)

    const handleSubmit = () => {
        if (!nomeEmpreendimento.trim()) { setError('Campo obrigatório'); return }
        const payload = { nome_empreendimento: nomeEmpreendimento }
        if (states.isEdit) actionEdit(states.Investimentos?.id, payload)
        else actionCreate(payload)
    }

    useEffect(() => {
        if (!states.modalInvestimentos) {
            setNomeEmpreendimento(''); setError(null)
        } else if (states.isEdit && states.Investimentos) {
            setNomeEmpreendimento(states.Investimentos.nome_empreendimento || '')
        }
    }, [states.modalInvestimentos])

    const isEdit = states.isEdit

    return (
        <Transition.Root show={states.modalInvestimentos} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalInvestimentos(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">

                                {/* ── Header ── */}
                                <div className="relative bg-gray-900 px-6 pt-6 pb-14 overflow-hidden">
                                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-yellow/10" />
                                    <div className="absolute -bottom-12 -left-6 w-32 h-32 rounded-full bg-brand-yellow/5" />

                                    <div className="relative flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                                                <BuildingOfficeIcon className="w-7 h-7 text-gray-900" />
                                            </div>
                                            <div>
                                                <span className={`inline-block mb-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest ${
                                                    isEdit ? 'bg-amber-400/20 text-amber-300' : 'bg-green-400/20 text-green-300'
                                                }`}>
                                                    {isEdit ? 'Modo edição' : 'Novo cadastro'}
                                                </span>
                                                <Dialog.Title as="h3" className="text-xl font-bold text-white leading-tight">
                                                    {isEdit ? 'Editar Investimento' : 'Novo Investimento'}
                                                </Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">
                                                    {isEdit
                                                        ? `Editando: ${states.Investimentos?.nome_empreendimento}`
                                                        : 'Cadastre um novo empreendimento'}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setters.setModalInvestimentos(false)}
                                            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* ── Form card ── */}
                                <div className="relative -mt-6 mx-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
                                        <label
                                            htmlFor="nomeEmpreendimento"
                                            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5"
                                        >
                                            <BuildingOfficeIcon className="w-3.5 h-3.5" />
                                            Nome do Empreendimento
                                        </label>
                                        <input
                                            id="nomeEmpreendimento"
                                            type="text"
                                            value={nomeEmpreendimento}
                                            onChange={e => { setNomeEmpreendimento(e.target.value); setError(null) }}
                                            placeholder="Ex: EMPREENDIMENTO-ALPHA"
                                            className={`mt-1.5 block w-full px-4 py-2.5 rounded-xl border text-sm transition-all duration-200 focus:outline-none focus:ring-2 ${
                                                error
                                                    ? 'border-red-300 bg-red-50/50 focus:ring-red-400 focus:border-red-400'
                                                    : 'border-gray-200 bg-white focus:ring-brand-yellow focus:border-brand-yellow'
                                            }`}
                                        />
                                        {error && (
                                            <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                                <span>⚠</span> {error}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* ── Footer ── */}
                                <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-6">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalInvestimentos(false)}
                                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                                    >
                                        {isEdit
                                            ? <><PencilSquareIcon className="w-4 h-4" /> Salvar Alterações</>
                                            : <><PlusCircleIcon className="w-4 h-4" /> Cadastrar</>}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalInvestimento
